import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { useMediaUpload } from "@/hooks/useMediaUpload";
import { useTRPC } from "@/lib/trpc";
import { useState } from "react";
import { Icons } from "../ui/icons";
import { MediaUpload } from "./media-upload";

// Form schema for the UI
const mediaFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
});

type MediaFormData = z.infer<typeof mediaFormSchema>;

type MediaFormProps =
  | {
      isEdit: true;
      initialValues: MediaFormData & {
        id: string;
        cloudStorageObjectId?: string;
        cloudStorageObject?: {
          fullUrl?: string;
        };
      };
    }
  | {
      isEdit: false;
      initialValues?: Partial<MediaFormData>;
    };

export function MediaForm({ isEdit, initialValues }: MediaFormProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const mediaId = isEdit ? initialValues.id : undefined;
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [cloudStorageObjectId, setCloudStorageObjectId] = useState<
    string | null
  >(isEdit ? initialValues.cloudStorageObjectId || null : null);

  const form = useForm<MediaFormData>({
    resolver: zodResolver(mediaFormSchema),
    defaultValues: {
      title: initialValues?.title || "",
      description: initialValues?.description || "",
    },
  });

  const { isUploading } = useMediaUpload({
    onSuccess: (media) => {
      setCloudStorageObjectId(media.cloudStorageObjectId);
      toast.success("File uploaded successfully");
    },
    onError: (error) => {
      toast.error(`Upload failed: ${error.message}`);
    },
  });

  const updateMediaMutation = useMutation({
    mutationFn: async (_data: MediaFormData & { id: string }) => {
      // For now, we'll just update through the existing API
      // In the future, this should use a TRPC update mutation
      toast.info("Update functionality coming soon");
      throw new Error("Update not implemented yet");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.media.list.queryOptions());
      navigate({ to: "/media" });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const isSubmitting = isUploading || updateMediaMutation.isPending;

  const onSubmit = async (data: MediaFormData) => {
    if (isEdit && mediaId) {
      updateMediaMutation.mutate({
        ...data,
        id: mediaId,
      });
    } else {
      // For new media, the upload process already handles everything
      if (!cloudStorageObjectId) {
        toast.error("Please upload a file first");
        return;
      }

      // The media record is already created by the upload process
      // Just navigate back
      queryClient.invalidateQueries(trpc.media.list.queryOptions());
      navigate({ to: "/media" });
      toast.success("Media created successfully");
    }
  };

  const handleFileUpload = async (storageId: string, file: File) => {
    setUploadedFile(file);
    setCloudStorageObjectId(storageId);

    // Update form values from file if not already set
    if (!form.getValues("title")) {
      form.setValue("title", file.name.replace(/\.[^/.]+$/, ""));
    }
  };

  const currentFileUrl =
    isEdit && initialValues.cloudStorageObject?.fullUrl
      ? initialValues.cloudStorageObject.fullUrl
      : null;

  return (
    <div className="mx-auto grid max-w-[59rem] flex-1 auto-rows-max gap-4 mt-4">
      <Form {...form}>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => {
              router.history.back();
            }}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Back</span>
          </Button>
          <h1 className="flex-1 shrink-0 whitespace-nowrap text-xl font-semibold tracking-tight sm:grow-0">
            {isEdit ? "Edit Media" : "Upload Media"}
          </h1>
          <div className="hidden items-center gap-2 md:ml-auto md:flex">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                router.history.back();
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={form.handleSubmit(onSubmit)}
              disabled={isSubmitting || (!isEdit && !cloudStorageObjectId)}
            >
              {isSubmitting ? <Icons.spinner className="h-4 w-4 mr-2" /> : null}
              {isEdit ? "Update" : "Create"} Media
            </Button>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Media Details</CardTitle>
                <CardDescription>
                  Enter the details for your media file.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter media title" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter media description"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>File Upload</CardTitle>
                <CardDescription>
                  {isEdit ? "Current file" : "Upload a new file"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!isEdit && (
                  <MediaUpload
                    onUpload={handleFileUpload}
                    accept="*/*"
                    currentFile={uploadedFile}
                    currentUrl={currentFileUrl}
                    disabled={isSubmitting}
                    title={form.watch("title")}
                    description={form.watch("description")}
                  />
                )}
                {isEdit && currentFileUrl && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Current file:
                    </p>
                    <a
                      href={currentFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline break-all"
                    >
                      {currentFileUrl}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 md:hidden mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              router.history.back();
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isSubmitting || (!isEdit && !cloudStorageObjectId)}
          >
            {isSubmitting ? <Icons.spinner className="h-4 w-4 mr-2" /> : null}
            {isEdit ? "Update" : "Create"} Media
          </Button>
        </div>
      </Form>
    </div>
  );
}
